/**
 * Face bounding box overlay component.
 * Displays a face detection box with controls for ignore/unignore and person assignment.
 *
 * COORDINATE SYSTEM:
 * Face coordinates are stored in XYXY format [x1, y1, x2, y2]:
 * - bbox: CROP-SPACE coords (relative to cropped preview)
 * - frame_bbox: FULL-FRAME coords (relative to original image)
 *
 * In VIZ mode: image shows full-frame -> use frame_bbox
 * In EDIT mode: image shows cropped preview -> use bbox directly (no transform)
 *
 * NOTE: Rotation handling is done in Seraph backend. frame_bbox is always
 * returned in original (non-rotated) coordinate space.
 *
 * MODE PROP:
 * - 'edit': Full interactivity (ignore, unignore, select, unassign buttons)
 * - 'view': Read-only display (shows boxes and names, no interactive buttons)
 */

// ---------- Helper functions for reduced complexity ----------

/**
 * Check if array is a valid bbox with 4 numeric elements
 */
function isValidBbox(arr) {
  return Array.isArray(arr) && arr.length === 4 && arr.every(v => typeof v === 'number');
}

/**
 * Extract coordinates for viz mode (full-frame)
 *
 * In viz mode, face coordinates are in ORIGINAL image space (e.g., 8640x5760).
 * Reference dimensions must be the original image dimensions, NOT the displayed
 * preview dimensions - percentage positioning handles scaling automatically.
 */
function getVizModeCoords(face, dims, fullFrameW, fullFrameH) {
  // Use frame_bbox (full-frame coords) if valid, otherwise fall back to bbox
  const frameBbox = isValidBbox(face.frame_bbox) ? face.frame_bbox : null;
  const bbox = isValidBbox(face.bbox) ? face.bbox : null;
  const [bx1 = 0, by1 = 0, bx2 = 0, by2 = 0] = frameBbox || bbox || [0, 0, 0, 0];

  return {
    fx1: bx1,
    fy1: by1,
    fx2: bx2,
    fy2: by2,
    // Reference must be ORIGINAL image dimensions (coordinate space of frame_bbox)
    // NOT displayed preview dimensions (that would break percentage calculations)
    refW: fullFrameW || dims.w || 1,
    refH: fullFrameH || dims.h || 1,
  };
}

/**
 * Determine reference dimensions for edit mode
 */
function getEditModeRefDims(actualCropW, actualCropH, cropBox, fullFrameW, fullFrameH, dims) {
  // Priority: actual crop dimensions > cropBox metadata > calculated crop > full-frame
  if (actualCropW && actualCropH) {
    return { refW: actualCropW, refH: actualCropH };
  }
  if (cropBox?.actualCropW && cropBox?.actualCropH) {
    return { refW: cropBox.actualCropW, refH: cropBox.actualCropH };
  }
  if (cropBox?.x2 > cropBox.x1 && cropBox.y2 > cropBox.y1) {
    return { refW: cropBox.x2 - cropBox.x1, refH: cropBox.y2 - cropBox.y1 };
  }
  // Last resort: full-frame (will be wrong if crop applied, but better than nothing)
  return { refW: fullFrameW || dims.w || 1, refH: fullFrameH || dims.h || 1 };
}

/**
 * Check if bbox fits within crop dimensions (indicates it's in crop-space)
 */
function bboxFitsInCrop(bbox, cropW, cropH) {
  if (!bbox || bbox.length !== 4) return false;
  const [x1, y1, x2, y2] = bbox;
  // Allow small margin for rounding errors
  const margin = 10;
  return x2 <= cropW + margin && y2 <= cropH + margin && x1 >= -margin && y1 >= -margin;
}

/**
 * Extract coordinates for edit mode (cropped preview)
 *
 * When displaying a cropped image, we need face coords in CROP-SPACE.
 *
 * Strategy: Crop preview is a CSS cut-out from the full preview (not edit.jpg),
 * so frame_bbox (original-image coords) is the native coordinate system.
 * We transform frame_bbox to crop-space by subtracting cropBox offset.
 * Falls back to bbox with actual crop dims if frame_bbox is unavailable.
 */
function getEditModeCoords(face, dims, actualCropW, actualCropH, cropBox, fullFrameW, fullFrameH) {
  const frameBbox = isValidBbox(face.frame_bbox) ? face.frame_bbox : null;
  const bbox = isValidBbox(face.bbox) ? face.bbox : null;

  const cropW = cropBox ? (cropBox.x2 - cropBox.x1) : null;
  const cropH = cropBox ? (cropBox.y2 - cropBox.y1) : null;

  // PREFER frame_bbox transformed to crop-space.
  // Crop preview is a CSS cut-out from the full preview, so frame_bbox
  // (original-image coords) with cropBox offset is the native coordinate system.
  if (cropBox && frameBbox && cropW && cropH && typeof cropBox.x1 === 'number') {
    const [fx1, fy1, fx2, fy2] = frameBbox;
    return {
      fx1: fx1 - cropBox.x1,
      fy1: fy1 - cropBox.y1,
      fx2: fx2 - cropBox.x1,
      fy2: fy2 - cropBox.y1,
      refW: cropW,
      refH: cropH,
    };
  }

  // Fallback: use bbox with actual crop dimensions (edit.jpg size from Seraph)
  const refCropW = actualCropW || cropW;
  const refCropH = actualCropH || cropH;
  if (bbox && refCropW && refCropH && bboxFitsInCrop(bbox, refCropW, refCropH)) {
    const [bx1, by1, bx2, by2] = bbox;
    return {
      fx1: bx1, fy1: by1, fx2: bx2, fy2: by2,
      refW: refCropW, refH: refCropH,
    };
  }

  // Last resort: use bbox directly with best-guess reference dims
  const [bx1 = 0, by1 = 0, bx2 = 0, by2 = 0] = bbox || frameBbox || [0, 0, 0, 0];
  const { refW, refH } = getEditModeRefDims(
    actualCropW, actualCropH, cropBox, fullFrameW, fullFrameH, dims
  );

  return { fx1: bx1, fy1: by1, fx2: bx2, fy2: by2, refW, refH };
}

/**
 * Compute face box styling properties
 */
function computeFaceBoxStyle(isIgnored, isTagged, isHovered) {
  const color = (() => {
    if (isIgnored) return '#888';
    if (isTagged) return '#66ffb7';
    return '#ff6b6b';
  })();
  const borderWidth = (() => {
    if (isIgnored) return 1;
    if (isTagged) return 1.5;
    return 2.5;
  })();
  return { color, borderWidth };
}

/**
 * Compute box size class for responsive button sizing
 */
function computeSizeClass(boxWidthPct, boxHeightPct) {
  if (boxWidthPct < 5 || boxHeightPct < 5) return 'face-tiny';
  if (boxWidthPct < 10 || boxHeightPct < 10) return 'face-small';
  return '';
}

/**
 * Compute glow effect based on selection/hover state
 */
function computeGlowEffect(selected, isHovered, isTagged) {
  if (selected) {
    const baseColor = isTagged ? 'rgba(104,255,154,0.35)' : 'rgba(255,107,107,0.35)';
    return `0 0 0 2px ${baseColor}`;
  }
  if (isHovered) {
    return '0 0 8px 2px rgba(102,255,183,0.6), 0 0 0 1px rgba(102,255,183,0.8)';
  }
  return 'none';
}

/**
 * Build full name from person data
 */
function buildFullName(face, personLabel) {
  const personData = face.person_data || face.person || null;
  const displayName = personLabel || face.person_display || face.person_name || '';
  const firstName = personData?.first_name || '';
  const lastName = personData?.last_name || '';
  return firstName && lastName ? `${firstName} ${lastName}` : displayName;
}

// ---------- Sub-components ----------

/**
 * Edit mode buttons (unignore, unassign, ignore)
 */
function FaceButtons({ face, isIgnored, isTagged, onUnignore, onIgnore, onSelect }) {
  if (isIgnored) {
    return (
      <div className="face-buttons">
        <button
          className="face-btn face-unignore"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onUnignore?.(face);
          }}
          aria-label="Restore face"
          title="Restore this face"
        >
          {'\u21A9'}
        </button>
      </div>
    );
  }

  return (
    <div className="face-buttons">
      {isTagged && (
        <button
          className="face-btn face-unassign"
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onSelect({ ...face, _unassign: true }, false);
          }}
          aria-label="Remove player link"
          title="Remove player link"
        >
          x
        </button>
      )}
      <button
        className="face-btn face-ignore"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onIgnore?.(face);
        }}
        aria-label="Ignore this face"
        title="Ignore this face (not subject)"
      >
        [-]
      </button>
    </div>
  );
}

// ---------- Main component ----------

export default function FaceOverlay({
  face,
  selected,
  isHovered,
  onHover,
  onSelect,
  onIgnore,
  onUnignore,
  dims,
  personLabel,
  cropBox,
  viewMode,
  imgBounds: _imgBounds,
  vizNaturalDims: _vizNaturalDims,
  // Mode: 'edit' (default, full interactivity) or 'view' (read-only display)
  mode = 'edit',
}) {
  // Get dimensions from face metadata (set by Seraph)
  const fullFrameW = face.image_width || face.meta?.image_width;
  const fullFrameH = face.image_height || face.meta?.image_height;
  const actualCropW = face.crop_width || face.meta?.crop_width;
  const actualCropH = face.crop_height || face.meta?.crop_height;

  // Get coordinates based on view mode
  const { fx1, fy1, fx2, fy2, refW, refH } = viewMode === 'viz'
    ? getVizModeCoords(face, dims, fullFrameW, fullFrameH)
    : getEditModeCoords(face, dims, actualCropW, actualCropH, cropBox, fullFrameW, fullFrameH);

  // Don't render if we don't have valid reference dimensions
  // (refW/refH of 1 means all fallbacks failed - would position face at 100000%+)
  if (refW <= 1 || refH <= 1) {
    return null;
  }

  // Don't render if we don't have valid face coordinates
  if (fx1 === 0 && fy1 === 0 && fx2 === 0 && fy2 === 0) {
    return null;
  }

  // Compute display state
  const hasPerson = !!(face.person || face.person_id);
  const isTagged = hasPerson || !!personLabel;
  const isIgnored = !!face.ignored;
  const isInteractive = mode === 'edit';

  // Compute styling
  const { color, borderWidth } = computeFaceBoxStyle(isIgnored, isTagged, isHovered);
  const boxWidthPct = ((fx2 - fx1) / refW) * 100;
  const boxHeightPct = ((fy2 - fy1) / refH) * 100;
  const sizeClass = computeSizeClass(boxWidthPct, boxHeightPct);
  const glowEffect = computeGlowEffect(selected, isHovered, isTagged);
  const fullName = buildFullName(face, personLabel);

  // Build style object
  const borderStyle = isIgnored ? 'dashed' : 'solid';
  const borderColor = isHovered ? '#66ffb7' : color;
  const style = {
    position: 'absolute',
    left: `${(fx1 / refW) * 100}%`,
    top: `${(fy1 / refH) * 100}%`,
    width: `${boxWidthPct}%`,
    height: `${boxHeightPct}%`,
    border: `${borderWidth}px ${borderStyle} ${borderColor}`,
    boxShadow: glowEffect,
    cursor: (isInteractive || isTagged) ? 'pointer' : 'default',
    opacity: isIgnored ? 0.6 : 1,
    transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
    pointerEvents: !isInteractive && isIgnored ? 'none' : 'auto',
  };

  return (
    <div
      className={`face-box ${sizeClass}`}
      style={style}
      onMouseEnter={() => onHover?.(face)}
      onMouseLeave={() => onHover?.(null)}
      onClick={() => isInteractive && !isIgnored && onSelect?.(face, true)}
    >
      {/* Top-right buttons - EDIT MODE ONLY */}
      {isInteractive && (
        <FaceButtons
          face={face}
          isIgnored={isIgnored}
          isTagged={isTagged}
          onUnignore={onUnignore}
          onIgnore={onIgnore}
          onSelect={onSelect}
        />
      )}
      {/* Player name label below the face box */}
      {fullName && !isIgnored && <div className="face-name-label">{fullName}</div>}
      {isIgnored && <div className="face-ignored-label">ignored</div>}
    </div>
  );
}
